import { useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Award,
  Search,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  ToggleLeft,
  ToggleRight,
  Tag,
} from 'lucide-react';
import {
  BADGE_UNIVERSOS,
  BADGE_UNIVERSO_COLORS,
  BADGE_MAX_LABELS,
  BADGE_MAX_LABEL_LENGTH,
  type BadgeUniverso,
} from 'shared';
import {
  useBadges,
  useCreateBadge,
  useUpdateBadge,
  useDeleteBadge,
  type Badge,
  type BadgeInput,
} from '@/hooks/useBadges';
import { ApiValidationError, ApiConflictError } from '@/lib/api';
import { useToast } from '@/components/useToast';
import { ToastContainer } from '@/components/Toast';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 20;

const EMPTY_FORM: BadgeInput = {
  name: '',
  description: null,
  active: true,
  universo: null,
  labels: [],
};

/**
 * Splits the comma-separated labels field into a clean array: trimmed, blanks
 * dropped, case-insensitive duplicates removed. Mirrors normalizeLabels in the
 * Go handler so the client preview matches what the server stores.
 */
function parseLabels(raw: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ─── Validation ───────────────────────────────────────────────────────────────

type FormErrors = Partial<Record<keyof BadgeInput, string>>;

function validate(form: BadgeInput): FormErrors {
  const e: FormErrors = {};
  if (!form.name.trim()) e.name = 'O nome da credencial é obrigatório.';
  else if (form.name.length > 200) e.name = 'Máximo de 200 caracteres.';

  const labels = form.labels ?? [];
  if (labels.length > BADGE_MAX_LABELS) {
    e.labels = `Máximo de ${BADGE_MAX_LABELS} etiquetas.`;
  } else if (labels.some(l => l.length > BADGE_MAX_LABEL_LENGTH)) {
    e.labels = `Cada etiqueta não pode exceder ${BADGE_MAX_LABEL_LENGTH} caracteres.`;
  }
  return e;
}

// ─── Universo Chip ────────────────────────────────────────────────────────────

function UniversoChip({ universo }: { universo: BadgeUniverso }) {
  const { bg, text } = BADGE_UNIVERSO_COLORS[universo];
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ backgroundColor: bg, color: text }}
    >
      {universo}
    </span>
  );
}

// ─── Active Badge ─────────────────────────────────────────────────────────────

function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      Ativa
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      Inativa
    </span>
  );
}

// ─── Form Field ───────────────────────────────────────────────────────────────

interface FieldProps {
  label: string;
  error?: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}

function FormField({ label, error, required, hint, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">
        {label}{required && <span className="text-destructive ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground/60">{hint}</p>}
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertTriangle className="w-3 h-3 shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Create / Edit Modal ──────────────────────────────────────────────────────

interface BadgeModalProps {
  mode: 'create' | 'edit';
  initial?: Badge;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function BadgeModal({ mode, initial, onClose, onSuccess, onError }: BadgeModalProps) {
  const create = useCreateBadge();
  const update = useUpdateBadge();

  const [form, setForm] = useState<BadgeInput>(
    initial
      ? {
          name: initial.name,
          description: initial.description ?? null,
          active: initial.active,
          universo: initial.universo ?? null,
          labels: initial.labels ?? [],
        }
      : EMPTY_FORM,
  );
  // The labels field is edited as raw comma-separated text so commas can be
  // typed freely; it is parsed into an array on submit.
  const [labelsText, setLabelsText] = useState(() => (initial?.labels ?? []).join(', '));
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverErrors, setServerErrors] = useState<FormErrors>({});

  const isPending = create.isPending || update.isPending;
  const fieldError = (key: keyof BadgeInput) => errors[key] || serverErrors[key];

  const set = <K extends keyof BadgeInput>(key: K, value: BadgeInput[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: undefined }));
    if (serverErrors[key]) setServerErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const setLabels = (value: string) => {
    setLabelsText(value);
    if (errors.labels) setErrors(prev => ({ ...prev, labels: undefined }));
    if (serverErrors.labels) setServerErrors(prev => ({ ...prev, labels: undefined }));
  };

  const parsedLabels = parseLabels(labelsText);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: BadgeInput = {
      ...form,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      labels: parsedLabels,
    };
    const clientErrors = validate(trimmed);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }
    try {
      if (mode === 'create') {
        await create.mutateAsync(trimmed);
        onSuccess('Credencial criada com sucesso.');
      } else {
        await update.mutateAsync({ id: initial!.id, data: trimmed });
        onSuccess('Credencial actualizada com sucesso.');
      }
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiValidationError) {
        setServerErrors(err.errors as FormErrors);
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro desconhecido.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="badge-modal-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" />
            <h2 id="badge-modal-title" className="text-lg font-semibold text-foreground">
              {mode === 'create' ? 'Nova Credencial' : 'Editar Credencial'}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form id="badge-form" onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4 overflow-y-auto">
          {/* Name */}
          <FormField label="Nome da Credencial" required error={fieldError('name')}>
            <input
              className={`input ${fieldError('name') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              maxLength={200}
              placeholder="ex. Certificação de Laser Cutter"
              aria-invalid={!!fieldError('name')}
              autoFocus
            />
            <div className="flex justify-end">
              <span className="text-[11px] text-muted-foreground/50 tabular-nums">{form.name.length}/200</span>
            </div>
          </FormField>

          {/* Description */}
          <FormField label="Descrição" error={fieldError('description')} hint="Explica como a credencial é obtida (opcional).">
            <textarea
              className="input resize-none h-24"
              value={form.description ?? ''}
              onChange={e => set('description', e.target.value || null)}
              placeholder="Descreve o processo de obtenção desta credencial…"
            />
          </FormField>

          {/* Universo */}
          <FormField
            label="Universo"
            error={fieldError('universo')}
            hint="Universo DIGGER a que a credencial pertence (opcional)."
          >
            <select
              className="input"
              value={form.universo ?? ''}
              onChange={e => set('universo', (e.target.value || null) as BadgeUniverso | null)}
              aria-invalid={!!fieldError('universo')}
            >
              <option value="">Sem universo</option>
              {BADGE_UNIVERSOS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </FormField>

          {/* Labels */}
          <FormField
            label="Etiquetas"
            error={fieldError('labels')}
            hint={`Separa as etiquetas por vírgulas. Máximo de ${BADGE_MAX_LABELS} (opcional).`}
          >
            <input
              className={`input ${fieldError('labels') ? 'border-destructive focus:ring-destructive/30' : ''}`}
              value={labelsText}
              onChange={e => setLabels(e.target.value)}
              placeholder="ex. soldadura, corte, iniciação"
              aria-invalid={!!fieldError('labels')}
            />
            {parsedLabels.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {parsedLabels.map(label => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                  >
                    <Tag className="w-3 h-3 shrink-0" />
                    {label}
                  </span>
                ))}
              </div>
            )}
          </FormField>

          {/* Active toggle */}
          <div className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/10">
            <div>
              <p className="text-sm font-medium text-foreground">Credencial Ativa</p>
              <p className="text-xs text-muted-foreground">
                Credenciais inativas não aparecem nos dropdowns de pré-requisito de equipamentos.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.active}
              onClick={() => set('active', !form.active)}
              className={`transition-colors ${form.active ? 'text-primary' : 'text-muted-foreground'}`}
            >
              {form.active
                ? <ToggleRight className="w-8 h-8" />
                : <ToggleLeft className="w-8 h-8" />
              }
            </button>
          </div>
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-border shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="badge-form"
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {mode === 'create' ? 'Criar Credencial' : 'Guardar Alterações'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delete Dialog ────────────────────────────────────────────────────────────

interface DeleteDialogProps {
  badge: Badge;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function DeleteDialog({ badge, onClose, onSuccess, onError }: DeleteDialogProps) {
  const del = useDeleteBadge();
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');
  const [conflictMessage, setConflictMessage] = useState('');

  const confirm = async () => {
    if (!reason.trim()) {
      setReasonError('O motivo de eliminação é obrigatório.');
      return;
    }
    try {
      await del.mutateAsync({ id: badge.id, reason: reason.trim() });
      onSuccess(`"${badge.name}" eliminada com sucesso.`);
      onClose();
    } catch (err: unknown) {
      if (err instanceof ApiConflictError) {
        setConflictMessage(err.message);
        return;
      }
      if (err instanceof ApiValidationError && err.errors['reason']) {
        setReasonError(err.errors['reason']);
        return;
      }
      onError(err instanceof Error ? err.message : 'Erro ao eliminar.');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="del-badge-title"
    >
      <div className="bg-card rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-destructive" />
          </div>
          <div>
            <h3 id="del-badge-title" className="text-base font-semibold text-foreground">
              Eliminar Credencial
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              Tens a certeza que queres eliminar{' '}
              <span className="font-semibold text-foreground">"{badge.name}"</span>?
              O registo ficará arquivado.
            </p>
          </div>
        </div>

        {/* 409 conflict banner */}
        {conflictMessage && (
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-800 dark:text-amber-300">{conflictMessage}</p>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-muted-foreground">
            Motivo de eliminação<span className="text-destructive ml-0.5">*</span>
          </label>
          <textarea
            className={`input resize-none h-20 ${reasonError ? 'border-destructive focus:ring-destructive/30' : ''}`}
            value={reason}
            onChange={e => { setReason(e.target.value); if (reasonError) setReasonError(''); }}
            placeholder="Descreve o motivo de eliminação…"
            aria-invalid={!!reasonError}
          />
          {reasonError && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertTriangle className="w-3 h-3 shrink-0" />
              {reasonError}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={del.isPending}
            className="px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent disabled:opacity-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={confirm}
            disabled={del.isPending || !!conflictMessage}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-destructive text-destructive-foreground text-sm font-medium hover:bg-destructive/90 disabled:opacity-50 transition-colors"
          >
            {del.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────────────────────────────────────────

interface DetailPanelProps {
  badge: Badge;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function DetailPanel({ badge, onClose, onEdit, onDelete }: DetailPanelProps) {
  const isDeleted = !!badge.deletedAt;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" aria-label="Detalhe da credencial">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="relative z-10 w-full max-w-md bg-card shadow-2xl flex flex-col h-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
              <Award className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground leading-tight">{badge.name}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                <ActiveBadge active={badge.active} />
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Fechar painel"
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-accent"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Deleted banner */}
          {isDeleted && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
              <AlertTriangle className="w-4 h-4 text-destructive mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-destructive">Registo eliminado</p>
                <p className="text-xs text-destructive/80 mt-0.5">{badge.deletionReason}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Em {formatDate(badge.deletedAt!)}
                </p>
              </div>
            </div>
          )}

          {/* Description */}
          {badge.description && (
            <div className="space-y-1.5">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Descrição</p>
              <p className="text-sm text-foreground whitespace-pre-line">{badge.description}</p>
            </div>
          )}

          {/* Universo */}
          {badge.universo && (
            <div className="space-y-1.5">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Universo</p>
              <UniversoChip universo={badge.universo} />
            </div>
          )}

          {/* Labels */}
          {!!badge.labels?.length && (
            <div className="space-y-1.5">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide font-medium">Etiquetas</p>
              <div className="flex flex-wrap gap-1">
                {badge.labels.map(label => (
                  <span
                    key={label}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground"
                  >
                    <Tag className="w-3 h-3 shrink-0" />
                    {label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Criada em</p>
              <p className="text-xs text-foreground">{formatDate(badge.createdAt)}</p>
            </div>
            <div className="p-3 rounded-lg border border-border bg-muted/10">
              <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">Actualizada em</p>
              <p className="text-xs text-foreground">{formatDate(badge.updatedAt)}</p>
            </div>
          </div>

          {/* UUID */}
          <div className="p-3 rounded-lg border border-border bg-muted/10">
            <p className="text-[11px] text-muted-foreground uppercase tracking-wide mb-1">ID</p>
            <p className="text-[11px] font-mono text-muted-foreground break-all">{badge.id}</p>
          </div>
        </div>

        {/* Footer — hidden for deleted records */}
        {!isDeleted && (
          <div className="flex items-center gap-3 px-6 py-4 border-t border-border shrink-0">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-md border border-border bg-card text-sm font-medium text-foreground hover:bg-accent transition-colors"
            >
              <Pencil className="w-4 h-4" />
              Editar
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-2 flex-1 justify-center px-4 py-2 rounded-md border border-destructive/30 bg-destructive/5 text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Empty State ──────────────────────────────────────────────────────────────

function EmptyState({ search, onCreate }: { search: string; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
        <Award className="w-7 h-7 text-muted-foreground/40" />
      </div>
      {search ? (
        <>
          <p className="text-base font-medium text-muted-foreground">
            Nenhum resultado para <span className="text-foreground">"{search}"</span>
          </p>
          <p className="text-sm text-muted-foreground/60">Tenta pesquisar por outro nome.</p>
        </>
      ) : (
        <>
          <p className="text-base font-medium text-muted-foreground">Sem credenciais registadas</p>
          <button
            onClick={onCreate}
            className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Criar primeira credencial
          </button>
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; badge: Badge }
  | { type: 'delete'; badge: Badge };

export default function BadgesPage() {
  const { toasts, toast, dismiss } = useToast();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [modal, setModal] = useState<ModalState>({ type: 'none' });

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 350);
  }, []);

  const { data, isLoading, isError, error, isFetching } = useBadges({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    search: debouncedSearch,
    activeOnly: filterActive === 'active',
    showDeleted,
  });

  const badges = data?.badges ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, total);

  const closeModal = () => setModal({ type: 'none' });

  const hasFilters = !!debouncedSearch || filterActive !== 'all';

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setFilterActive('all');
    setPage(0);
  };

  return (
    <>
      <Helmet>
        <title>Credenciais | DIGGER</title>
      </Helmet>

      <div className="space-y-6">
        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Award className="w-6 h-6 text-primary" />
              Credenciais
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestão das credenciais associáveis a utilizadores e requeridas por equipamentos.
            </p>
          </div>
          <button
            onClick={() => setModal({ type: 'create' })}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors shadow-sm shrink-0"
          >
            <Plus className="w-4 h-4" />
            Nova Credencial
          </button>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative min-w-56 max-w-xs flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <input
              type="search"
              placeholder="Pesquisar por nome…"
              className="input pl-9 w-full"
              value={search}
              onChange={e => handleSearch(e.target.value)}
              aria-label="Pesquisar credenciais"
            />
          </div>

          {/* Active filter */}
          <div className="flex items-center gap-1 p-1 rounded-md border border-border bg-muted/30">
            {(['all', 'active', 'inactive'] as const).map(v => (
              <button
                key={v}
                onClick={() => { setFilterActive(v); setPage(0); }}
                className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                  filterActive === v
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {v === 'all' ? 'Todas' : v === 'active' ? 'Ativas' : 'Inativas'}
              </button>
            ))}
          </div>

          {/* Show deleted toggle */}
          <button
            onClick={() => { setShowDeleted(v => !v); setPage(0); }}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${
              showDeleted
                ? 'border-destructive/40 bg-destructive/10 text-destructive'
                : 'border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground'
            }`}
            aria-pressed={showDeleted}
          >
            {showDeleted ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {showDeleted ? 'Ocultar eliminadas' : 'Mostrar eliminadas'}
          </button>

          {/* Count + clear */}
          <div className="flex items-center gap-2 ml-auto">
            {hasFilters && (
              <button
                onClick={resetFilters}
                className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
              >
                Limpar filtros
              </button>
            )}
            {total > 0 && (
              <span className="text-sm text-muted-foreground tabular-nums shrink-0">
                {total} credencial{total !== 1 ? 'is' : ''}
                {isFetching && !isLoading && (
                  <Loader2 className="inline-block w-3 h-3 ml-1.5 animate-spin text-primary" />
                )}
              </span>
            )}
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="card overflow-hidden">
          {isLoading && (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
          )}

          {isError && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <AlertTriangle className="w-8 h-8 text-destructive" />
              <p className="text-sm font-medium text-destructive">
                {error?.message ?? 'Erro ao carregar credenciais.'}
              </p>
            </div>
          )}

          {!isLoading && !isError && (
            <>
              {badges.length === 0 ? (
                <EmptyState search={debouncedSearch} onCreate={() => setModal({ type: 'create' })} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[720px] text-sm" aria-label="Lista de credenciais">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Nome</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Descrição</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Universo</th>
                        <th scope="col" className="px-4 py-3 text-left font-medium text-muted-foreground">Estado</th>
                        <th scope="col" className="px-4 py-3 w-20 text-right font-medium text-muted-foreground">Acções</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {badges.map(b => {
                        const isDeletedRow = !!b.deletedAt;
                        return (
                          <tr
                            key={b.id}
                            className={`hover:bg-accent/30 transition-colors cursor-pointer ${isDeletedRow ? 'opacity-60' : ''}`}
                            onClick={() => setSelectedBadge(b)}
                          >
                            {/* Name */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                                  <Award className="w-4 h-4 text-primary" />
                                </div>
                                <div className="min-w-0">
                                  <span className="font-medium text-foreground">{b.name}</span>
                                  {!!b.labels?.length && (
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {b.labels.map(label => (
                                        <span
                                          key={label}
                                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground"
                                        >
                                          {label}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Description preview */}
                            <td className="px-4 py-3 text-muted-foreground max-w-xs">
                              <p className="truncate">{b.description ?? '—'}</p>
                            </td>

                            {/* Universo */}
                            <td className="px-4 py-3">
                              {b.universo
                                ? <UniversoChip universo={b.universo} />
                                : <span className="text-muted-foreground">—</span>
                              }
                            </td>

                            {/* State */}
                            <td className="px-4 py-3">
                              {isDeletedRow
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Eliminada</span>
                                : <ActiveBadge active={b.active} />
                              }
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3">
                              <div
                                className="flex items-center gap-1 justify-end"
                                onClick={e => e.stopPropagation()}
                              >
                                {!isDeletedRow && (
                                  <>
                                    <button
                                      onClick={() => setModal({ type: 'edit', badge: b })}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                                      title={`Editar ${b.name}`}
                                      aria-label={`Editar ${b.name}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => setModal({ type: 'delete', badge: b })}
                                      className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                      title={`Eliminar ${b.name}`}
                                      aria-label={`Eliminar ${b.name}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Pagination ── */}
              {total > PAGE_SIZE && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
                  <span className="text-sm text-muted-foreground tabular-nums">
                    {from}–{to} de {total}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setPage(p => Math.max(0, p - 1))}
                      disabled={page === 0}
                      aria-label="Página anterior"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let p: number;
                        if (totalPages <= 5) p = i;
                        else if (page < 3) p = i;
                        else if (page >= totalPages - 3) p = totalPages - 5 + i;
                        else p = page - 2 + i;
                        return (
                          <button
                            key={p}
                            onClick={() => setPage(p)}
                            aria-label={`Página ${p + 1}`}
                            aria-current={p === page ? 'page' : undefined}
                            className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${
                              p === page
                                ? 'bg-primary text-primary-foreground'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                            }`}
                          >
                            {p + 1}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                      disabled={page >= totalPages - 1}
                      aria-label="Próxima página"
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Detail Panel ── */}
      {selectedBadge && (
        <DetailPanel
          badge={selectedBadge}
          onClose={() => setSelectedBadge(null)}
          onEdit={() => {
            setModal({ type: 'edit', badge: selectedBadge });
            setSelectedBadge(null);
          }}
          onDelete={() => {
            setModal({ type: 'delete', badge: selectedBadge });
            setSelectedBadge(null);
          }}
        />
      )}

      {/* ── Modals ── */}
      {modal.type === 'create' && (
        <BadgeModal
          mode="create"
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'edit' && (
        <BadgeModal
          mode="edit"
          initial={modal.badge}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}
      {modal.type === 'delete' && (
        <DeleteDialog
          badge={modal.badge}
          onClose={closeModal}
          onSuccess={msg => toast.success(msg)}
          onError={msg => toast.error(msg)}
        />
      )}

      {/* ── Toasts ── */}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </>
  );
}
